import { useEffect, useState, type RefObject } from 'react'
import { isLookupSelection, normalizeLookupText, type TextLookupSelection } from '../data/vocabularyLookup'

const elementForNode = (node: Node) => node instanceof Element ? node : node.parentElement

const excerptAroundRange = (surface: Element, range: Range) => {
  const text = surface.textContent?.replace(/\s+/g, ' ').trim() ?? ''
  if (!text) return ''
  const before = range.cloneRange()
  before.selectNodeContents(surface)
  before.setEnd(range.startContainer, range.startOffset)
  const start = before.toString().replace(/\s+/g, ' ').length
  const segmenter = typeof Intl.Segmenter === 'function'
    ? new Intl.Segmenter('en', { granularity: 'sentence' })
    : undefined
  const sentence = segmenter
    ? Array.from(segmenter.segment(text)).find((part) => start >= part.index && start <= part.index + part.segment.length)?.segment
    : text.split(/(?<=[.!?])\s+/).find((part) => part.includes(range.toString().trim()))
  const candidate = (sentence ?? text).trim()
  if (candidate.length <= 320) return candidate
  const selected = range.toString().trim()
  const selectedIndex = candidate.toLocaleLowerCase().indexOf(selected.toLocaleLowerCase())
  const from = Math.max(0, Math.min(candidate.length - 320, selectedIndex - 120))
  return `${from > 0 ? '…' : ''}${candidate.slice(from, from + 320).trim()}${from + 320 < candidate.length ? '…' : ''}`
}

export const readTextLookupSelection = (root: HTMLElement): TextLookupSelection | undefined => {
  const nativeSelection = window.getSelection()
  if (!nativeSelection || nativeSelection.rangeCount !== 1 || nativeSelection.isCollapsed) return undefined
  const range = nativeSelection.getRangeAt(0)
  const startSurface = elementForNode(range.startContainer)?.closest('[data-lookup-text="true"]')
  const endSurface = elementForNode(range.endContainer)?.closest('[data-lookup-text="true"]')
  if (!startSurface || startSurface !== endSurface || !root.contains(startSurface)) return undefined
  const term = range.toString().normalize('NFKC').trim().replace(/\s+/g, ' ')
  if (!isLookupSelection(term)) return undefined
  const clientRects = typeof range.getClientRects === 'function'
    ? Array.from(range.getClientRects()).filter((candidate) => candidate.width > 0 && candidate.height > 0)
    : []
  const rect = clientRects.at(-1) ?? (typeof range.getBoundingClientRect === 'function' ? range.getBoundingClientRect() : undefined)
  if (!rect) return undefined
  const viewport = window.visualViewport
  const pageLeft = viewport?.pageLeft ?? window.scrollX
  const pageTop = viewport?.pageTop ?? window.scrollY
  return {
    term: term.replace(/^[\[\]{}()<>“”"'.,:;!?]+|[\[\]{}()<>“”"'.,:;!?]+$/g, ''),
    normalized: normalizeLookupText(term),
    contextExcerpt: excerptAroundRange(startSurface, range),
    anchor: {
      left: pageLeft + rect.left + rect.width / 2,
      top: pageTop + rect.top,
      bottom: pageTop + rect.bottom,
    },
  }
}

export function useTextLookupSelection(rootRef: RefObject<HTMLElement | null>) {
  const [selection, setSelection] = useState<TextLookupSelection>()

  useEffect(() => {
    let timeout: number | undefined
    let frame: number | undefined
    const read = () => {
      const root = rootRef.current
      setSelection(root ? readTextLookupSelection(root) : undefined)
    }
    const update = () => {
      window.clearTimeout(timeout)
      timeout = window.setTimeout(read, 80)
    }
    const updatePosition = () => {
      if (frame !== undefined) window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(read)
    }
    const viewport = window.visualViewport
    document.addEventListener('selectionchange', update)
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    viewport?.addEventListener('resize', updatePosition)
    viewport?.addEventListener('scroll', updatePosition)
    return () => {
      window.clearTimeout(timeout)
      if (frame !== undefined) window.cancelAnimationFrame(frame)
      document.removeEventListener('selectionchange', update)
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
      viewport?.removeEventListener('resize', updatePosition)
      viewport?.removeEventListener('scroll', updatePosition)
    }
  }, [rootRef])

  const clear = () => {
    setSelection(undefined)
    window.getSelection()?.removeAllRanges()
  }
  return { selection, clear }
}
